import type { TFunction } from 'i18next';

import type { ActiveFilterChip } from '@/features/header-filters';
import type { SelectOption } from '@/shared/ui';

import { getDateRangeOptions } from '../config/filter-options';

type MaybeChip = ActiveFilterChip | null | false;

/** Keeps the chips that turned out to be active, in the order they were listed. */
export const collectChips = (...chips: MaybeChip[]): ActiveFilterChip[] =>
  chips.filter((chip): chip is ActiveFilterChip => Boolean(chip));

const labelOf = (options: SelectOption[], value: string) =>
  options.find((option) => option.value === value)?.label ?? value;

/**
 * Builds chips for the single-choice filters of one dashboard.
 *
 * A dashboard select always carries a value — Metric is Weight, Group by is Date
 * — so what makes a filter active is differing from its default, the same test
 * `useUrlState` applies before a parameter reaches the URL.
 *
 * Reading the value and the default off the same key is what keeps a chip from
 * ever naming one filter while removing another.
 */
export const optionChips =
  <S extends Record<string, string | undefined>>(state: S, defaults: S, onRemove: (key: string) => void) =>
  (key: Extract<keyof S, string>, options: SelectOption[]): ActiveFilterChip | null => {
    const value = state[key];

    if (!value || value === defaults[key]) {
      return null;
    }

    return { key, label: labelOf(options, value), onRemove: () => onRemove(key) };
  };

interface DateRangeChipArgs {
  period: string | undefined;
  fromDate: string | undefined;
  toDate: string | undefined;
  defaultPeriod: string | undefined;
  t: TFunction;
  onRemove: (key: string) => void;
}

/**
 * The preset and the two custom dates are one filter to the reader, so they
 * collapse into one chip. Removing it clears all three, or a stale `fromDate`
 * would keep narrowing the query with nothing on screen to say so.
 */
export const dateRangeChip = ({
  period,
  fromDate,
  toDate,
  defaultPeriod,
  t,
  onRemove
}: DateRangeChipArgs): ActiveFilterChip | null => {
  const chip = (label: string) => ({ key: 'date', label, onRemove: () => onRemove('date') });

  if (fromDate || toDate) {
    return chip([fromDate, toDate].filter(Boolean).join(' – '));
  }

  if (period && period !== defaultPeriod) {
    return chip(labelOf(getDateRangeOptions(t), period));
  }

  return null;
};

export interface SelectionOption {
  id: string;
  name: string;
}

interface SelectionChipArgs {
  key: string;
  /** What the filter is called; used once there is more than one pick to name. */
  title: string;
  selected: string[];
  available: SelectionOption[];
  onRemove: () => void;
}

/**
 * One chip for a whole multi-select, counting the picks rather than listing them
 * — a project carries dozens of locations, and a chip each would bury the row.
 * A lone pick is named outright, since a count would say less than the name.
 */
const summarise = ({ key, title, selected, available, onRemove }: SelectionChipArgs): ActiveFilterChip => ({
  key,
  label:
    selected.length === 1
      ? (available.find((option) => option.id === selected[0])?.name ?? selected[0])
      : `${title}: ${selected.length}`,
  onRemove
});

/**
 * For a multi-select that starts with everything picked, where only a narrowing
 * is a filter — Locations, Organizations. Comparing against the offered options is what tells
 * "all of them" apart from a genuine subset.
 */
export const subsetChip = (args: SelectionChipArgs): ActiveFilterChip | null => {
  const { selected, available } = args;
  const isEverything = available.length > 0 && selected.length === available.length;

  if (!selected.length || isEverything) {
    return null;
  }

  return summarise(args);
};
